import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Linkedin, Instagram, Twitter } from "lucide-react";
import { submitEnquiry } from "@/lib/api";
import { personal } from "@/data/portfolio";


const SOCIALS = [
  { id: "email", label: "Email", value: personal.email, href: `mailto:${personal.email}`, Icon: Mail, testid: "contact-email-link" },
  { id: "linkedin", label: "LinkedIn", value: "/in/deepanshu-k", href: personal.linkedin, Icon: Linkedin, testid: "contact-linkedin-link", external: true },
  { id: "instagram", label: "Instagram", value: "@ayushjha_ig", href: personal.instagram, Icon: Instagram, testid: "contact-instagram-link", external: true },
  { id: "twitter", label: "X / Twitter", value: "@ayushjha_x", href: personal.twitter, Icon: Twitter, testid: "contact-twitter-link", external: true },
  { id: "behance", label: "Behance", value: "/ayushportfolio", href: personal.behance, Icon: BehanceIcon, testid: "contact-behance-link", external: true },
];

function BehanceIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 18} height={props.size || 18} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 5h6.5a3 3 0 0 1 0 6H2zM2 11h7a3 3 0 0 1 0 6H2zM14 13a3 3 0 1 1 6 0H14a3 3 0 0 0 6 0M15 6h4" />
    </svg>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", project: "Graphic Design", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: "Please fill name, email and message." });
      return;
    }
    setStatus({ state: "loading", msg: "Sending…" });
    try {
      await submitEnquiry("/contact", form);
      setStatus({ state: "ok", msg: "Thanks — your brief has been saved. I'll reply within 24 hours." });
      setForm({ name: "", email: "", project: "Graphic Design", message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: `${err.message || "Couldn't send right now."} Email me at workwithayush858@gmail.com.` });
    }
  };

  return (
    <section id="contact" className="section contact" data-testid="contact-section">
      <div className="contact__grid">
        <div className="contact__left">
          <span className="overline">◆ Let&apos;s talk · 01 form</span>
          <h2 className="contact__title serif" data-testid="contact-heading">
            Got a brief?
            <br />
            <span className="italic">Tell me about it.</span>
          </h2>
          <p className="contact__lede">
            Available for freelance projects, retainers and community builds. The faster you write, the faster I design.
          </p>

          <div className="contact__channels" data-testid="contact-channels">
            {SOCIALS.map(({ id, label, value, href, Icon, testid, external }) => (
              <a
                key={id}
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noreferrer" : undefined}
                className="ctile"
                data-testid={testid}
              >
                <Icon size={18} strokeWidth={1.4} />
                <div>
                  <span className="overline">{label}</span>
                  <span className="ctile__val">{value}</span>
                </div>
                <ArrowUpRight size={18} className="ctile__arrow" />
              </a>
            ))}
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="contact__form"
          data-testid="contact-form"
        >
          <div className="field">
            <label className="overline" htmlFor="contact-name">01 — Your name</label>
            <input id="contact-name" name="name" maxLength={120} autoComplete="name" type="text" value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="contact-name-input" required />
          </div>
          <div className="field">
            <label className="overline" htmlFor="contact-email">02 — Email</label>
            <input id="contact-email" name="email" maxLength={254} autoComplete="email" type="email" value={form.email} onChange={set("email")} placeholder="hello@company.com" data-testid="contact-email-input" required />
          </div>
          <div className="field">
            <span id="project-label" className="overline">03 — Project type</span>
            <div className="chips" role="radiogroup" aria-labelledby="project-label" data-testid="contact-project-chips">
              {["Graphic Design", "YouTube Thumbnails", "Social Campaign", "Community Build", "Other"].map((p) => (
                <label key={p} className={`chip ${form.project === p ? "chip--on" : ""}`}>
                  <input type="radio" name="project" value={p} checked={form.project === p} onChange={() => setForm({ ...form, project: p })} />
                  {p}
                </label>
              ))}
            </div>
          </div>
          <div className="field">
            <label className="overline" htmlFor="contact-message">04 — The brief</label>
            <textarea id="contact-message" name="message" maxLength={5000} autoComplete="off" rows={5} value={form.message} onChange={set("message")} placeholder="What are we building? Timeline, audience, vibe — any details help." data-testid="contact-message-input" required />
          </div>

          <button type="submit" className="submit-btn" disabled={status.state === "loading"} data-testid="contact-submit-btn">
            <span>{status.state === "loading" ? "Sending…" : "Send the brief"}</span>
            <ArrowUpRight size={18} strokeWidth={1.5} />
          </button>

          {status.msg && (
            <div className={`form-status form-status--${status.state}`} data-testid="contact-status-message" role="status">
              {status.msg}
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}
